import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// ──────────────────────────────────────────────────────────────────────────
// UiContext — reemplazo tipado de los window.CustomEvent que usaba el sidebar
// para comunicarse con GestionPanel.
//
// Patrón: un contador por "señal" (focusSearch, toggleCriticos, abrirChecklist).
// El productor (SidebarNav) llama a emit('focusSearch'). El consumidor
// (GestionPanel) usa useUiSignal('focusSearch', handler) y el handler se
// ejecuta cada vez que el contador aumenta.
//
// Ventajas sobre CustomEvent:
//  - Sin strings mágicos globales.
//  - Si el consumidor no está montado, las señales previas no se disparan
//    cuando aparezca (semántica clara, sin estado fantasma en window).
//  - Tipable y fácil de rastrear con devtools de React.
// ──────────────────────────────────────────────────────────────────────────

const UiContext = createContext(null);

const SIGNALS = ['focusSearch', 'toggleCriticos', 'abrirChecklist'];

const THEME_KEY = 'vera.theme';
// Sprint 15.3: el usuario puede elegir explícitamente entre 3 modos.
//   'light' / 'dark' = elección explícita, NO sigue el sistema
//   'auto'           = sigue prefers-color-scheme del sistema
// Si nunca eligió, el default es 'auto'. Cuando elige uno explícito,
// se guarda en localStorage y queda fijo hasta que vuelva a 'auto'.
const VALID_MODES = ['light', 'dark', 'auto'];

function systemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function initialMode() {
  if (typeof window === 'undefined') return 'auto';
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (VALID_MODES.includes(saved)) return saved;
  } catch (_) { /* ignore */ }
  return 'auto';
}

export function UiProvider({ children }) {
  const [counters, setCounters] = useState(() =>
    Object.fromEntries(SIGNALS.map((s) => [s, 0]))
  );

  // themeMode: lo que eligió el usuario ('light' | 'dark' | 'auto').
  // theme: lo que efectivamente se aplica al DOM (resuelve 'auto' a 'light'/'dark').
  const [themeMode, setThemeMode] = useState(initialMode);
  const [systemPref, setSystemPref] = useState(systemTheme);
  const theme = themeMode === 'auto' ? systemPref : themeMode;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Escuchamos cambios del sistema SIEMPRE — incluso si el modo es explícito,
  // queremos saber el system pref por si el user vuelve a 'auto'.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemPref(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // setThemeMode persiste la elección del usuario en localStorage.
  // Pasar 'auto' lo borra (default).
  const setMode = useCallback((mode) => {
    if (!VALID_MODES.includes(mode)) return;
    setThemeMode(mode);
    try {
      if (mode === 'auto') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, mode);
    } catch (_) { /* ignore */ }
  }, []);

  // Compatibilidad con código legacy que usa setTheme/toggleTheme.
  // setTheme acepta 'light'|'dark' y persiste como elección explícita.
  const setTheme = useCallback((t) => setMode(t === 'dark' ? 'dark' : 'light'), [setMode]);
  // toggleTheme cicla light → dark → auto → light. Se mantiene la API
  // anterior pero ahora con 3 estados.
  const toggleTheme = useCallback(() => {
    const next = themeMode === 'light' ? 'dark' : themeMode === 'dark' ? 'auto' : 'light';
    setMode(next);
  }, [themeMode, setMode]);

  const emit = useCallback((signal) => {
    if (!SIGNALS.includes(signal)) {
      console.warn('[UiContext] signal desconocida:', signal);
      return;
    }
    setCounters((prev) => ({ ...prev, [signal]: prev[signal] + 1 }));
  }, []);

  return (
    <UiContext.Provider value={{ counters, emit, theme, themeMode, systemPref, setTheme, setMode, toggleTheme }}>
      {children}
    </UiContext.Provider>
  );
}

export function useUi() {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi debe usarse dentro de <UiProvider>');
  return ctx;
}

// Hook de suscripción. Dispara `handler` cada vez que se emite `signal`.
// Ignora la primera emisión (valor inicial) para no ejecutar en mount.
export function useUiSignal(signal, handler) {
  const { counters } = useUi();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const count = counters[signal];
  const prev = useRef(count);

  useEffect(() => {
    if (count !== prev.current) {
      prev.current = count;
      handlerRef.current?.();
    }
  }, [count]);
}
